import moment, { Moment } from 'moment';
import { appStore } from 'src/mobx/appStore';
import { branchStore } from 'src/mobx/barnchStore';
import { cartStore } from 'src/mobx/cartStore';
import { localeStore } from 'src/mobx/localesStore';
import { IProductInsideCombo } from 'src/types/combo';
import { TTranslate } from 'src/types/main';
import {
  IProduct,
  IProductExclusion,
  IProductModifier,
  IProductModifierOption,
  IProductSize,
  IResponseProduct,
  IResponseProductModifier,
  IResponseProductModifierOption,
  PRODUCT_TYPE,
} from 'src/types/products';
import { ITimeSlotHour } from 'src/types/time';
import { getRadix, isCorrectProductSize, isSameComboItems, logError } from './helpers';
import { hash } from './helpers/hash';

/**
 * Function for price rounding
 *
 * @param {(number | string)} price - price to be rounded up
 * @param {number} [divided=100] - how much to round the price in currency
 * @return {number | string} {number | string}
 */
export function roundPrice(price: number | string, divided: number = 100): number | string {
  /**
   * Bit depth of the number to which the price is rounded
   *  @type {*}
   */
  const numberOfDigits = getRadix(divided);

  if (typeof price === 'number' && !isNaN(price)) return Number(price.toFixed(numberOfDigits));

  if (typeof price === 'string') {
    const _price = Number(price);
    if (typeof _price === 'number' && !isNaN(_price)) return Number(_price.toFixed(numberOfDigits));
  }

  if (!price) return 0;

  return price;
}

/**
 * Formats the cost of the product depending on the currency and currency position
 *
 * Special characters are used for correct display currency
 *
 * @param {number} price - price number
 * @param {(string | undefined)} [currency= branchStore.currency] - currecy of price
 * @param {string} [position= branchStore.currency_position] - position of currency
 * @param {string} [devided= branchStore.currency_divided] - how much to round the price in currency
 * @return {string} - formated price
 */
export const formatPrice = (
  price: number | string,
  currency: string | undefined = branchStore.currency,
  position: string = branchStore.currency_position,
  divided: number = branchStore.currency_divided
) => {
  if (position === 'l') {
    return `\u202a${getTranslate(currency)} \u202a${roundPrice(price, divided)}\u202c`;
  }
  return `\u202A${roundPrice(price, divided)} \u202a${getTranslate(currency)}\u202c`;
};

export const formatUnit = (translate: string, weight: number): string | null => {
  if (!translate || translate === '-') return null;
  return `${weight} ${localeStore.t(translate)}`;
};

const getProductQuantity = (product: IProduct | IResponseProduct): number => {
  if (product.quantity && product.quantity > 0) {
    if (product.quantity > product.min_quantity) return product.quantity;
    return product.min_quantity;
  }
  if (product.min_quantity && product.min_quantity > 0) return product.min_quantity;
  return 1;
};

export const transformItems = (product: IResponseProduct | IProduct): IProduct['items'] => {
  if (!product.type || !product.items) {
    return product.items;
  }

  if (product.type === PRODUCT_TYPE.DEAL_COMBO) {
    // select first deal combo
    let selectedIdx = product.items?.findIndex((item) => item.is_default);
    if (!~selectedIdx) selectedIdx = 0;

    return product.items?.map((item, index) => ({ ...item, is_selected: Number(index === selectedIdx) }));
  }

  return product.items;
};

export const _transformProduct = (
  product: IResponseProduct,
  storedProduct?: IProduct,
  ignoreDefaultModifiers: boolean = cartStore.isSMS
): IProduct => {
  return {
    ...product,
    //  select prevision selected size OR select first size OR leave empty
    images: product.images ? [product.image, ...product.images] : [product.image],
    hash: storedProduct ? storedProduct.hash : '',
    notes: storedProduct ? storedProduct.notes : product.notes,
    quantity: getProductQuantity(storedProduct || product),
    items: transformItems(storedProduct || product),
    size: storedProduct
      ? storedProduct.size
      : product.sizes.find((size: any) => size.is_selected) || product.sizes[0] || null,
    modifiers: product.modifiers.map((modifier) => {
      return _transformModifier(modifier, storedProduct?.modifiers, ignoreDefaultModifiers);
    }),
    exclusions: product.exclusions.map((exclusion: any) => {
      const findExclusion = storedProduct?.exclusions?.find((i) => i.id === exclusion.id);
      return {
        ...exclusion,
        selected: findExclusion?.selected || exclusion.is_selected,
      };
    }),
  };
};

export const _transformModifier = (
  _modifier: IResponseProductModifier,
  storedModifiers?: IProductModifier[],
  ignoreDefaultModifiers?: boolean
): IProductModifier => {
  let is_preselected = false;
  const findStoredModifier = storedModifiers?.find((i) => i.id === _modifier.id);

  const modifier = {
    ..._modifier,
    options: _modifier.options.map((option: IResponseProductModifierOption): IProductModifierOption => {
      const findStoredOption = findStoredModifier?.options.find((i) => i.id === option.id);
      if (findStoredOption?.selected || option.is_selected) is_preselected = true;

      return {
        ...option,
        selected: findStoredOption?.selected || Boolean(option.is_selected),
        quantity: getOptionQuantity(option, _modifier.unique_options, findStoredOption),
        maximum: option.maximum || 99, // 0 = unlimited
        minimum: option.minimum || 1,
      };
    }),
  };

  /** When modifier type changes from multi to single */
  if (!modifier.multi_choice && modifier.options.filter((i) => i.selected).length > 1) {
    is_preselected = false;
    modifier.options = modifier.options.map((i) => ({ ...i, selected: false, quantity: 1 }));
  }

  // do not select default options if it was preselected (FOR REORDER)
  if (is_preselected) return modifier;

  // Ignore default modifier
  if (ignoreDefaultModifiers) {
    modifier.options = modifier.options.map((option) => ({ ...option, selected: false }));
    return modifier;
  }

  // is single choise
  if (!modifier.multi_choice) {
    // find default option
    const defaultOptionIdx = modifier.options.findIndex((option) => option.is_default);
    if (~defaultOptionIdx) {
      // select default option
      modifier.options[defaultOptionIdx].selected = true;
    } else {
      // select fiirst option
      modifier.options[0].selected = true;
    }
  } else {
    // if multy choise
    modifier.options = modifier.options.map((option) => ({ ...option, selected: Boolean(option.is_default) }));
  }
  return modifier;
};

const getOptionQuantity = (
  option: IResponseProductModifierOption,
  unique_options: number,
  storedOption?: IProductModifierOption
) => {
  const _option = storedOption || option;
  if (unique_options) {
    return 1;
  } else if (_option && option.maximum !== 0 && _option?.quantity > option.maximum) {
    return option.maximum;
  } else {
    return _option.quantity || option.minimum || 1;
  }
};

export const getProductMainPrice = (product: IProduct) =>
  product.size && product.size.id ? product.size.price : product.price;

export const hashProduct = (product: IProduct) => {
  const json = {
    id: product.id,
    options: product.modifiers.reduce(
      (ids: { id: number; quantity: number }[], modifiers: IProductModifier): { id: number; quantity: number }[] => {
        modifiers.options.forEach((option: IProductModifierOption) => {
          if (option.selected) {
            ids.push({
              id: option.id,
              quantity: option.quantity,
            });
          }
        });
        return ids;
      },
      []
    ),
    exclusions: product.exclusions.reduce((ids: number[], exclusion: IProductExclusion) => {
      if (exclusion.selected) {
        ids.push(exclusion.id);
      }
      return ids;
    }, []),
    items: product.items
      ? product.items.reduce((ids: string[], current: IProductInsideCombo) => {
          if (
            current.is_selected &&
            isCorrectProductSize(product.size) &&
            current.size_id === (product.size as IProductSize)?.id
          ) {
            ids.push(current.id + '' + current.size_id + current.group_id);
          }
          return ids;
        }, [])
      : null,
    size: product.size ? product.size : null,
    notes: product.notes ? product.notes : null,
  };
  return hash(JSON.stringify(json));
};

export const formatImagePath = (name: string) => `${process.env.REACT_APP_CLOUD_FRONT}/media/images/${name}`;

/**
 * Transform products/exclusions/modifiers/options in cart with updated properties from calculator
 * error, price, etc
 *
 * @param {IProduct[]} products
 * @param {IProduct[]} productsFromCalculator
 * @return {*}
 */
export const transformCalculatedProducts = (products: IProduct[], productsFromCalculator: IProduct[]) => {
  try {
    const updatedProducts = products.map((product: IProduct, index) => {
      const findProduct = productsFromCalculator.find((item: IProduct, calculatedIndex) => index === calculatedIndex);
      if (findProduct) {
        product.error = findProduct.error ? findProduct.error[0] : '';
        product.price = findProduct.price;
        product.orderable = 'orderable' in findProduct ? findProduct.orderable : true;

        if (findProduct.modifiers?.length > 0) {
          product.modifiers = transformCalculatedModifiers(findProduct.modifiers, product.modifiers);
        }

        if (findProduct.exclusions && product.exclusions && product.exclusions.length > 0) {
          product.exclusions = transformCalculatedExclusions(findProduct.exclusions, product.exclusions);
        }

        if (findProduct.sizes && product.size && product.size.id) {
          product.size = transformCalculatedSize(findProduct.sizes, product.size);
        }

        if (findProduct.type) {
          product.items = transformCalculatedItems(
            findProduct.items || [],
            product.items || [],
            findProduct.type === PRODUCT_TYPE.MEAL_COMBO
          );
        }
      }
      return product;
    });

    const haveErrors = updatedProducts.find((item: IProduct) => {
      if (item.error || !item.orderable) return true;

      const errorInExclusions = item.exclusions ? item.exclusions.find((exclusion) => Boolean(exclusion.error)) : false;
      if (errorInExclusions) return true;

      const errorInSize = item.size ? Boolean(item.size.error) : false;
      if (errorInSize) return true;

      if (item.type && item.items?.length) {
        const errorInItems = item.items.find((_item) => Boolean(_item.error));
        if (errorInItems) return true;
      }

      const errorInModifiers = item.modifiers
        ? item.modifiers.find((modifier) => {
            if (Boolean(modifier.error)) return true;
            const errorInOptions = modifier.options ? modifier.options.find((option) => Boolean(option.error)) : false;
            return Boolean(errorInOptions);
          })
        : false;

      return !!errorInModifiers;
    });

    return { updatedProducts, haveErrors };
  } catch (err) {
    logError(err);
    return { updatedProducts: products, haveErrors: false };
  }
};

const transformCalculatedExclusions = (
  exclusionsFromCalculator: IProductExclusion[],
  exclusions: IProductExclusion[]
) => {
  return exclusions.map((exclusion: IProductExclusion) => {
    const findExclusion = exclusionsFromCalculator.find((item: IProductExclusion) => item.id === exclusion.id);
    if (findExclusion) {
      exclusion.error = findExclusion.error ? findExclusion.error[0] : '';
    }
    return exclusion;
  });
};

const transformCalculatedModifiers = (modifiersFromCalculator: IProductModifier[], modifiers: IProductModifier[]) => {
  return modifiers.map((modifier: IProductModifier) => {
    const findModifier = modifiersFromCalculator.find((item: IProductModifier) => item.id === modifier.id);
    if (findModifier) {
      modifier.error = findModifier.error ? findModifier.error[0] : '';
      modifier.options = transformCalculatedOptions(findModifier.options, modifier.options);
    }
    return modifier;
  });
};

const transformCalculatedOptions = (
  optionsFromCalculator: IProductModifierOption[],
  options: IProductModifierOption[]
) => {
  return options.map((option: IProductModifierOption) => {
    const findOption = optionsFromCalculator.find((item: IProductModifierOption) => item.id === option.id);
    if (findOption) {
      option.price = findOption.price;
      option.error = findOption.error ? findOption.error[0] : '';
    }
    return option;
  });
};

const transformCalculatedSize = (sizesFromCalculator: IProductSize[], size: IProductSize) => {
  const findSize = sizesFromCalculator.find((item) => item.id === size.id);
  return findSize
    ? {
        ...size,
        price: findSize.price,
        error: findSize.error,
      }
    : size;
};

const transformCalculatedItems = (
  itemsFromCalculator: IProductInsideCombo[],
  items: IProductInsideCombo[],
  isMealCombo: boolean
) => {
  if (isMealCombo) {
    return items.map((item) => {
      const findItem: IProductInsideCombo | undefined = itemsFromCalculator.find((_item) =>
        isSameComboItems(_item, item)
      );
      if (findItem) {
        item.price = findItem.price;
        item.error = findItem.error ? findItem.error[0] : '';
      }
      return item;
    });
  }

  // deal items
  return itemsFromCalculator.map((item) => {
    return { ...item, error: item.error ? item.error[0] : '' };
  });
};

export const arrayMerge = <T>(main: T[], incoming: T[], field?: string): T[] => {
  const compare = (a: any, b: any) => (field ? a[field] === b[field] : a === b);
  return incoming.reduce((_main, i: T) => {
    const idx = _main.findIndex((_i) => compare(_i, i));
    if (~idx) {
      _main[idx] = i;
    } else {
      _main.push(i);
    }
    return _main;
  }, main);
};

export const getTranslate = (translate?: TTranslate | string) => {
  if (!translate) return '';
  if (typeof translate === 'string') return translate;
  return translate ? translate[localeStore.language] : '';
};

export const getSocketHost = (): string => {
  if (!appStore.settings?.socket) return '';
  let host = decodeURIComponent(appStore.settings.socket);
  if (!host.includes('https:')) host = `https://${host}`;
  return host;
};

export const getHostName = (origin: string) => origin.split(/https:\/\/|http:\/\/|:|\./)[1];

export const formatTime = (
  time: string | Moment,
  parser: string | undefined = 'hh:mm A',
  format: string | undefined = 'hh:mm'
) => {
  // TOFIX AM/PM
  const m = moment(time, parser, 'en');
  const AMPM = m.format('A');
  return `${m.format(format)} ${['AM', 'PM'].includes(AMPM) ? localeStore.t(AMPM) : AMPM}`;
};

/**
 * Format hours from {@link TimeController} to times
 *
 * @param {ITimeSlotHour[]} hours
 * @return {*}
 */
export const formatHoursToTimes = (hours: ITimeSlotHour[]) => {
  return hours.flatMap((hour) => {
    return hour.label === 'Now'
      ? [{ label: hour.label, moment: hour.moment, index: hour.index }]
      : hour.minutes.map((minute) => {
          return { ...minute, label: formatTime(minute.moment.format('hh:mm A')) };
        });
  });
};

export const formatPreorderDate = (time: Moment) => {
  const now = moment();

  if (time.isAfter(now, 'year')) {
    return time.format("MMM.DD 'YY");
  } else if (time.isSame(now, 'date')) {
    return localeStore.t('txt_today');
  } else if (time.isSame(now.clone().add(1, 'day'), 'date')) {
    return localeStore.t('txt_tomorrow');
  } else if (time.isBefore(now.clone().add(7, 'day'), 'date')) {
    return time.format('dddd');
  } else if (time.isSameOrAfter(now.clone().add(7, 'day'), 'date')) {
    return time.format('dd, MMM.D');
  } else {
    return time.format('ddd, DD MMM');
  }
};
